import type { Option } from '@/constants/institute';
import { cn } from '@/lib/utils';
import { FieldNote } from './FieldNote';

export interface CheckboxGroupProps {
  label: string;
  options: readonly Option[];
  value: string[];
  onChange: (value: string[]) => void;
  error?: string;
  hint?: string;
  /** Two columns on wider screens — for long lists like modules or mediums. */
  columns?: 1 | 2 | 3;
}

const COLUMNS = {
  1: 'sm:grid-cols-1',
  2: 'sm:grid-cols-2',
  3: 'sm:grid-cols-2 lg:grid-cols-3',
} as const;

/**
 * Multi-select as a grid of checkboxes rather than a multi-select dropdown.
 *
 * Everything is visible at once, it works with a thumb on a phone, and it is
 * the only pattern that reads clearly for "which modules did this institute buy"
 * — where the unticked options matter as much as the ticked ones.
 */
export function CheckboxGroup({
  label,
  options,
  value,
  onChange,
  error,
  hint,
  columns = 2,
}: CheckboxGroupProps) {
  function toggle(optionValue: string) {
    onChange(
      value.includes(optionValue)
        ? value.filter((item) => item !== optionValue)
        : [...value, optionValue],
    );
  }

  return (
    <fieldset className="flex flex-col gap-1.5">
      <legend className="lbl mb-1.5">{label}</legend>

      <div className={cn('grid grid-cols-1 gap-2', COLUMNS[columns])}>
        {options.map((option) => {
          const isChecked = value.includes(option.value);
          return (
            <label
              key={option.value}
              className={cn(
                'flex cursor-pointer items-center gap-2.5 rounded-field border px-3 py-2.5 text-sm transition-colors',
                isChecked
                  ? 'border-accent-line bg-accent-soft text-ink'
                  : 'border-line bg-surface text-ink-2 hover:border-line-strong',
              )}
            >
              <input
                type="checkbox"
                checked={isChecked}
                onChange={() => toggle(option.value)}
                className="h-4 w-4 shrink-0 accent-accent"
              />
              <span className="min-w-0 truncate">{option.label}</span>
            </label>
          );
        })}
      </div>

      <FieldNote error={error} hint={hint} />
    </fieldset>
  );
}
